import React from "react";
import { MessageSquareOff } from "lucide-react";
import { Thread } from "../../pages/comms/Messages/Thread";

/**
 * The coaching thread for one client, or an explanation when they have none.
 *
 * Shared by the chat dock and the Users split view's Chat tab. Deliberately
 * lives here rather than in `common/comms`: this is coach-only content, and
 * `ClientProfilePanel` — the support-agent drawer — must never reach a coaching
 * room. A shared location would invite exactly that import.
 *
 * Mount discipline: `Thread` drives `useRoomMessages`, which holds a room
 * channel subscription, a poll fallback while the socket is down, and a
 * debounced read receipt. Callers must mount this only while the chat is
 * actually visible — `RoomsUnreadProvider` already watches every room for the
 * badge (and feeds `roomMessagesCache` in the background), so a hidden chat
 * needs no live connection of its own, and a mounted-but-hidden `Thread` would
 * quietly clear unread counts for messages nobody read.
 *
 * @param {Object} props
 * @param {string|null} props.roomId - null renders the no-room state.
 * @param {string} props.viewerUserId
 * @param {number} [props.reloadNonce=0] - Bumping forces a full `Thread`
 *   remount, which is what a chat refresh means: `useRoomMessages` refetches
 *   room, participants, messages and reads on mount.
 */
export function DockChatBody({ roomId, viewerUserId, reloadNonce = 0 }) {
  if (!roomId) return <NoRoomEmptyState />;

  return (
    <Thread
      key={`${roomId}:${reloadNonce}`}
      roomId={roomId}
      viewerUserId={viewerUserId}
    />
  );
}

/** Shown in place of a thread for a client with no coaching room. */
function NoRoomEmptyState() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
      <MessageSquareOff className="h-8 w-8 text-gray-300" />
      <p className="text-sm font-medium text-gray-600">No coaching room yet</p>
      <p className="max-w-[15rem] text-xs text-gray-400">
        A chat opens once this client has a coaching room. Notes are available
        now.
      </p>
    </div>
  );
}

export default DockChatBody;
